/**
 * Home page content.
 *
 * Every string in this file is transcribed verbatim from the approved
 * "Home Page (SEO Optimized 2026)" requirement document. Do not reword,
 * shorten or extend this copy — update the document first.
 */

export const heroContent = {
  h1: "Empowering Businesses Through Certification, Compliance & Sustainability",
  paragraphs: [
    "MBA Solutions is a professional management consultancy helping organizations achieve internationally recognized certifications, strengthen compliance systems and improve business performance.",
    "With expertise across ISO standards, food safety, sustainability, textile compliance and supply chain certifications, we support businesses from initial assessment to successful certification.",
    "Our experienced consultants help organizations build practical management systems that create long-term value, improve efficiency and meet global market requirements.",
  ],
  cta: { label: "Get Your Free Consultation", to: "/contact" },
};

export const aboutContent = {
  title: "About MBA Solutions",
  subtitle: "Building Better Businesses Through Global Standards",
  paragraphs: [
    "At MBA Solutions, we believe certifications are more than just documents — they are tools for improving business performance, reducing risks and creating sustainable growth.",
    "Established as a management consultancy organization, we provide expert guidance in certification implementation, compliance improvement, training and audit preparation.",
    "Our team works with organizations across different industries including manufacturing, food, textile, IT, hospitality and export sectors.",
  ],
  cta: { label: "Learn More About Us", to: "/about" },
};

/* `image` is the category's illustration in public/assets/core-services — not
   copy, so it is not part of the approved-document transcription above. */
export const coreServicesContent = {
  title: "Our Core Services",
  categories: [
    {
      id: "iso-certification-consultancy",
      to: "/iso-certification-services",
      image: "/assets/core-services/iso-certification-consultancy.webp",
      logoKeys: ["iso-9001", "iso-14001", "iso-45001", "iso-22000"],
      icon: "certificate",
      title: "ISO Certification Consultancy",
      intro:
        "We help organizations implement and achieve internationally recognized ISO management systems including:",
      items: [
        "ISO 9001 Quality Management System",
        "ISO 14001 Environmental Management System",
        "ISO 45001 Occupational Health & Safety",
        "ISO 22000 Food Safety Management System",
        "ISO 27001 Information Security Management System",
        "ISO 50001 Energy Management System",
      ],
      outro: null,
    },
    {
      id: "food-safety-compliance-solutions",
      to: "/food-safety-services",
      image: "/assets/core-services/food-safety-compliance-solutions.webp",
      logoKeys: ["haccp", "halal", "fssc-22000"],
      icon: "food",
      title: "Food Safety & Compliance Solutions",
      intro:
        "MBA Solutions supports food businesses with globally accepted food safety standards:",
      items: [
        "HACCP Certification",
        "Halal Certification",
        "FSSC 22000",
        "BRCGS Food Safety",
        "GMP & GHP Implementation",
      ],
      outro:
        "We help food manufacturers improve safety controls, meet buyer requirements and access global markets.",
    },
    {
      id: "sustainability-esg-consulting",
      to: "/sustainability-esg-services",
      image: "/assets/core-services/sustainability-esg-consulting.webp",
      logoKeys: [],
      icon: "sustainability",
      title: "Sustainability & ESG Consulting",
      intro: "We support businesses in achieving sustainability goals through:",
      items: [
        "EcoVadis Preparation",
        "ESG Consulting",
        "GRI Reporting",
        "SA8000 Compliance",
        "Sustainable Business Practices",
      ],
      outro:
        "Our solutions help organizations improve environmental, social and governance performance.",
    },
    {
      id: "textile-supply-chain-compliance",
      to: "/textile-compliance-services",
      image: "/assets/core-services/textile-supply-chain-compliance.webp",
      logoKeys: [],
      icon: "textile",
      title: "Textile & Supply Chain Compliance",
      intro:
        "For textile manufacturers and exporters, we provide consultancy for:",
      items: [
        "GOTS Certification",
        "GRS Certification",
        "RCS & OCS",
        "WRAP",
        "Sedex / SMETA",
        "FSC Chain of Custody",
      ],
      outro:
        "Helping businesses meet international buyer expectations and responsible sourcing requirements.",
    },
  ],
};

/* `image` is the reason's illustration in public/assets/why-choose — not copy,
   so it is not part of the approved-document transcription above. `icon` is
   kept alongside it: the registry entry is still the fallback if an
   illustration is ever dropped, and other sections share the same keys. */
export const whyChooseContent = {
  title: "Why Choose MBA Solutions?",
  subtitle: "Trusted Certification & Compliance Partner",
  reasons: [
    {
      icon: "experience",
      image: "/assets/why-choose/proven-experience.webp",
      title: "Proven Experience",
      description:
        "Supporting organizations with professional consultancy and implementation expertise.",
    },
    {
      icon: "industry",
      image: "/assets/why-choose/industry-knowledge.webp",
      title: "Industry Knowledge",
      description:
        "Our solutions are designed according to real business operations and industry requirements.",
    },
    {
      icon: "support",
      image: "/assets/why-choose/complete-certification-support.webp",
      title: "Complete Certification Support",
      description:
        "From gap assessment to documentation, implementation and audit preparation.",
    },
    {
      icon: "global",
      image: "/assets/why-choose/global-standards-expertise.webp",
      title: "Global Standards Expertise",
      description:
        "Helping businesses align with internationally recognized frameworks.",
    },
    {
      icon: "improvement",
      image: "/assets/why-choose/long-term-business-improvement.webp",
      title: "Long-Term Business Improvement",
      description:
        "We focus on building systems that continue delivering value after certification.",
    },
  ],
};

/* `image` is the step's illustration in public/assets/certification-process —
   not copy, so it is not part of the approved-document transcription above. */
export const processContent = {
  title: "Our Certification Process",
  subtitle: "A Simple Path Toward Successful Certification",
  steps: [
    {
      number: "1",
      image: "/assets/certification-process/consultation.webp",
      icon: "consultation",
      title: "Consultation",
      description:
        "Understanding your business goals, processes and certification requirements.",
    },
    {
      number: "2",
      image: "/assets/certification-process/gap-assessment.webp",
      icon: "gap",
      title: "Gap Assessment",
      description: "Identifying current gaps against the required standard.",
    },
    {
      number: "3",
      image: "/assets/certification-process/documentation.webp",
      icon: "documentation",
      title: "Documentation",
      description:
        "Developing required policies, procedures and management system documents.",
    },
    {
      number: "4",
      image: "/assets/certification-process/implementation.webp",
      icon: "implementation",
      title: "Implementation",
      description:
        "Supporting your team in applying the required controls and processes.",
    },
    {
      number: "5",
      image: "/assets/certification-process/internal-audit.webp",
      icon: "audit",
      title: "Internal Audit",
      description:
        "Checking system effectiveness and preparing your organization.",
    },
    {
      number: "6",
      image: "/assets/certification-process/certification-support.webp",
      icon: "certification",
      title: "Certification Support",
      description:
        "Providing guidance during the certification audit process.",
    },
  ],
};

export const industriesContent = {
  title: "Industries We Serve",
  intro: "MBA Solutions provides specialized consultancy for:",
  industries: [
    { icon: "food", name: "Food & Beverage Industry", to: "/industries-we-serve#food-beverage" },
    { icon: "textile", name: "Textile & Garments", to: "/industries-we-serve#textile-garments" },
    { icon: "it", name: "IT & Software Companies", to: "/industries-we-serve#it-software" },
    { icon: "manufacturing", name: "Manufacturing Organizations", to: "/industries-we-serve#manufacturing" },
    { icon: "engineering", name: "Engineering Sector", to: "/industries-we-serve#engineering" },
    { icon: "hospitality", name: "Hospitality & Tourism", to: "/industries-we-serve#hospitality-tourism" },
    { icon: "export", name: "Export Businesses", to: "/industries-we-serve#export-businesses" },
    { icon: "supplyChain", name: "Supply Chain Organizations", to: "/industries-we-serve#supply-chain-logistics" },
  ],
};

export const commitmentContent = {
  title: "Our Commitment",
  subtitle: "Helping Businesses Grow With Confidence",
  paragraph:
    "We combine technical expertise, practical implementation experience and industry understanding to help businesses achieve compliance, improve performance and compete in global markets.",
};

/* "FAQ Section" — the four questions supplied 2026-09-28, verbatim. */
export const homeFaqContent = [
  {
    question: "What services does MBA Solutions provide?",
    answer:
      "MBA Solutions provides ISO certification consultancy, food safety solutions, sustainability consulting, textile compliance and supply chain certification support.",
  },
  {
    question: "Do you support complete certification implementation?",
    answer:
      "Yes, we support organizations from initial assessment, documentation and implementation to audit preparation.",
  },
  {
    question: "Which industries do you work with?",
    answer:
      "We work with food, textile, manufacturing, IT, engineering, hospitality and export businesses.",
  },
  {
    question: "Why choose MBA Solutions?",
    answer:
      "We provide practical, industry-focused consultancy solutions designed to help businesses achieve compliance and improve performance.",
  },
];

export const finalCtaContent = {
  title: "Ready to Start Your Certification Journey?",
  paragraph:
    "Partner with MBA Solutions for professional certification consultancy, compliance solutions and sustainability support.",
  cta: { label: "Contact MBA Solutions Today", to: "/contact" },
};
